import React from 'react'
import classNames from 'classnames'
import MoneyComponent from 'uiComponents/money'
import { useSelector } from 'react-redux'
import { getActiveAccountSlug } from 'auth/selectors'
import { useTranslation } from 'translations'

interface MoneyProps {
    amount?: number
    className?: string
}

const Money = ({ amount, className }: MoneyProps) => {
    const accountSlug = useSelector(getActiveAccountSlug)
    if (!accountSlug || amount == null) {
        return null
    }
    return (
        <span className={className}>
            <MoneyComponent amount={amount} accountSlug={accountSlug} />
        </span>
    )
}

export interface PricingPlanSettingsProps {
    renewal?: string
    contract?: string
    annualTotal?: number
    upfrontTotal?: number
    monthlyTotal?: number
    downPayment?: number
    monthly?: number
}

interface SettingProps {
    label: string
    visible?: boolean
    children?: React.ReactNode
}

const Setting = ({ label, visible = true, children }: SettingProps) => (
    <div className="setting">
        <label className={classNames({ hidden: !visible })}>{label}</label>
        {visible && children}
    </div>
)

// One shared 6-column grid for both contract lengths, so the columns of
// 2-year rows line up with the 1-year rows around them.
export const PricingPlanSettings = ({
    renewal,
    contract,
    annualTotal,
    upfrontTotal,
    monthlyTotal,
    downPayment,
    monthly,
}: PricingPlanSettingsProps) => {
    const { t } = useTranslation()
    const isTwoYearContract = !!contract

    return (
        <div className="pricing-plan-settings">
            <Setting label={t('products.pricing.pricingPlans.settings.contract')} visible={isTwoYearContract}>
                <span className="contract value">{contract}</span>
            </Setting>
            <Setting label={t('products.pricing.pricingPlans.settings.autoRenewal')} visible={!!renewal}>
                <span className="renewal value">{renewal}</span>
            </Setting>
            <Setting
                label={
                    isTwoYearContract
                        ? t('products.pricing.pricingPlans.settings.perYear')
                        : t('products.pricing.pricingPlans.settings.annualTotal')
                }
                visible={annualTotal != null}
            >
                <Money amount={annualTotal} className="annual-total-price value" />
            </Setting>
            {isTwoYearContract ? (
                <Setting label={t('products.pricing.pricingPlans.settings.twoYearsTotal')} visible={upfrontTotal != null}>
                    <Money amount={upfrontTotal} className="upfront-total-price value" />
                </Setting>
            ) : (
                <Setting label={t('products.pricing.pricingPlans.settings.monthlyTotal')} visible={monthlyTotal != null}>
                    <Money amount={monthlyTotal} className="monthly-total-price value" />
                </Setting>
            )}
            <Setting
                label={t('products.pricing.pricingPlans.settings.initial')}
                visible={!isTwoYearContract && downPayment != null}
            >
                <Money amount={downPayment} className="down-payment value" />
            </Setting>
            <Setting
                label={t('products.pricing.pricingPlans.settings.perMonth')}
                visible={!isTwoYearContract && monthly != null}
            >
                <Money amount={monthly} className="monthly-installment value" />
            </Setting>
        </div>
    )
}
